import * as React from 'react'
import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useDynamicSurveyOptions } from '@/hooks/useDynamicSurveyOptions'
import type { OptionSource } from '@/types/survey'

type SingleProps = {
  multiple?: false
  value: string
  onChange: (value: string) => void
  /** Single-select: persist the chosen row's label across server-side filtering. */
  selectedLabel?: string
  onSelectedLabelChange?: (label: string | undefined) => void
}

type MultiProps = {
  multiple: true
  value: string[]
  onChange: (value: string[]) => void
  /** Multi-select: keep a {value: label} map so chips render even after re-fetches. */
  selectedLabel?: Record<string, string>
  onSelectedLabelChange?: (labels: Record<string, string>) => void
}

type SearchableDynamicDropdownProps = (SingleProps | MultiProps) & {
  snapshotId: string | null | undefined
  questionCode: string
  optionSource: OptionSource
  /** Answers keyed by question code — already narrowed to depends_on parents. */
  dependencyAnswers: Record<string, unknown>
  language: 'en' | 'ar'
  placeholder: string
  disabled?: boolean
  hasError?: boolean
  /**
   * Reports whether the (unfiltered) option set for the current dependency has
   * any rows. Fires false while awaiting a parent answer, and is NOT updated
   * while a search term is active (a filtered-to-empty view ≠ no options).
   * Used by cascading questions (e.g. neighborhood) to make "required" depend
   * on whether the chosen parent actually has options.
   */
  onAvailabilityChange?: (available: boolean) => void
}

/**
 * Searchable dropdown for ``option_source`` survey questions. Renders a
 * Popover with a debounced server-side search — no full list is ever
 * shipped to the client, so a 4000-row table stays cheap.
 *
 * Supports single and multi-select via the ``multiple`` prop. In multi-mode,
 * the value is a string[] of ids and the trigger shows chips for each
 * selection; clicking an option toggles it without closing the popover.
 */
export const SearchableDynamicDropdown: React.FC<SearchableDynamicDropdownProps> = (props) => {
  const {
    snapshotId,
    questionCode,
    optionSource,
    dependencyAnswers,
    language,
    placeholder,
    disabled = false,
    hasError = false,
    onAvailabilityChange,
  } = props

  const isMulti = props.multiple === true
  const isAr = language === 'ar'
  const [open, setOpen] = React.useState(false)
  const [searchInput, setSearchInput] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')

  React.useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput), 250)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const { options, isLoading, error, awaitingParent } = useDynamicSurveyOptions({
    snapshotId,
    questionCode,
    optionSource,
    currentAnswers: dependencyAnswers,
    language,
    search: debouncedSearch.trim() || undefined,
  })

  const selectedValues: string[] = React.useMemo(() => {
    if (isMulti) {
      const v = (props as MultiProps).value
      return Array.isArray(v) ? v.filter(Boolean) : []
    }
    const v = (props as SingleProps).value
    return v ? [v] : []
  }, [isMulti, props])

  // Surface freshly-seen labels up to the parent so chips/triggers persist
  // even after the search filter hides the row from the visible page.
  React.useEffect(() => {
    if (selectedValues.length === 0) return
    if (isMulti) {
      const current = ((props as MultiProps).selectedLabel || {}) as Record<string, string>
      let next: Record<string, string> | null = null
      for (const v of selectedValues) {
        const match = options.find(o => o.value === v)
        if (match && current[v] !== match.label) {
          next = next || { ...current }
          next[v] = match.label
        }
      }
      if (next) (props as MultiProps).onSelectedLabelChange?.(next)
    } else {
      const v = selectedValues[0]
      const match = options.find(o => o.value === v)
      const current = (props as SingleProps).selectedLabel
      if (match && match.label !== current) {
        (props as SingleProps).onSelectedLabelChange?.(match.label)
      }
    }
  }, [selectedValues, options, isMulti, props])

  React.useEffect(() => {
    if (!open) setSearchInput('')
  }, [open])

  // Surface whether the parent's option set is non-empty. Skip transient
  // states (loading/error) and any active search so a filtered-to-zero view
  // isn't mistaken for "no options". No parent answer yet → not available.
  React.useEffect(() => {
    if (!onAvailabilityChange) return
    if (awaitingParent) {
      onAvailabilityChange(false)
      return
    }
    if (isLoading || error || debouncedSearch.trim()) return
    onAvailabilityChange(options.length > 0)
  }, [onAvailabilityChange, awaitingParent, isLoading, error, debouncedSearch, options.length])

  const removeValue = (val: string) => {
    if (!isMulti) return
    const p = props as MultiProps
    const next = (p.value || []).filter(v => v !== val)
    p.onChange(next)
    if (p.selectedLabel && p.selectedLabel[val]) {
      const { [val]: _removed, ...rest } = p.selectedLabel
      p.onSelectedLabelChange?.(rest)
    }
  }

  const toggleOption = (option: { value: string; label: string }) => {
    if (isMulti) {
      const p = props as MultiProps
      const current = p.value || []
      const isSelected = current.includes(option.value)
      const next = isSelected
        ? current.filter(v => v !== option.value)
        : [...current, option.value]
      p.onChange(next)
      const labels = { ...(p.selectedLabel || {}) }
      if (isSelected) delete labels[option.value]
      else labels[option.value] = option.label
      p.onSelectedLabelChange?.(labels)
      return
    }
    const p = props as SingleProps
    p.onChange(option.value)
    p.onSelectedLabelChange?.(option.label)
    setOpen(false)
  }

  const labelFor = (val: string): string => {
    if (isMulti) {
      const map = (props as MultiProps).selectedLabel || {}
      return map[val] || val
    }
    return (props as SingleProps).selectedLabel || (isAr ? 'تم الاختيار' : 'Selected')
  }

  const triggerContent = (() => {
    if (selectedValues.length === 0) {
      return awaitingParent
        ? (isAr ? 'اختر القيمة السابقة أولاً' : 'Pick the previous answer first')
        : placeholder
    }
    if (!isMulti) {
      return labelFor(selectedValues[0])
    }
    return null // multi: rendered as chips below
  })()

  const isTriggerDisabled = disabled || awaitingParent

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={isTriggerDisabled}
          className={cn(
            'w-full justify-between font-normal min-h-9 h-auto py-1.5',
            !selectedValues.length && 'text-muted-foreground',
            hasError && 'border-destructive',
          )}
        >
          {isMulti && selectedValues.length > 0 ? (
            <div className="flex flex-wrap gap-1 flex-1 text-start">
              {selectedValues.map(val => (
                <span
                  key={val}
                  className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="truncate max-w-[12rem]">{labelFor(val)}</span>
                  {!isTriggerDisabled && (
                    <span
                      role="button"
                      aria-label={isAr ? 'إزالة' : 'Remove'}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        removeValue(val)
                      }}
                      className="rounded-sm hover:bg-muted"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <span className="truncate text-start">{triggerContent}</span>
          )}
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        sideOffset={4}
      >
        <div className="border-b p-2">
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={isAr ? 'ابحث...' : 'Search...'}
            className="h-9"
            autoFocus
          />
        </div>
        <div
          role="listbox"
          aria-multiselectable={isMulti || undefined}
          className="max-h-64 overflow-y-auto py-1"
          dir={isAr ? 'rtl' : 'ltr'}
        >
          {isLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isAr ? 'جارٍ التحميل...' : 'Loading...'}
            </div>
          )}
          {!isLoading && error && (
            <div className="px-3 py-4 text-sm text-destructive">
              {isAr ? 'فشل تحميل الخيارات' : 'Failed to load options'}
            </div>
          )}
          {!isLoading && !error && options.length === 0 && (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              {debouncedSearch
                ? (isAr ? 'لا توجد نتائج' : 'No results')
                : (isAr ? 'لا توجد خيارات' : 'No options')}
            </div>
          )}
          {!isLoading && !error && options.map(option => {
            const isSelected = selectedValues.includes(option.value)
            return (
              <button
                type="button"
                role="option"
                aria-selected={isSelected}
                key={option.value}
                onClick={() => toggleOption(option)}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-start text-sm hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none',
                  isSelected && 'bg-accent/50',
                )}
              >
                <span className="truncate">{option.label}</span>
                {isSelected && <Check className="ms-2 h-4 w-4 shrink-0" />}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
